import useStore from 'state/knovStore'
import { getClientShare } from 'lib/bsv-signing'
import {
    loadLocalWalletStatus,
    NotLocalWalletAccountError,
    OPEN_WALLET_SETUP_EVENT,
} from 'lib/localWallet'
import { CLIENT_SIGNING_READY_EVENT, currentUserUsesClientSigning } from 'lib/clientSigningFeature'
import {
    advanceWalletPasskeyStep,
    loadWalletPasskeyStatus,
    pickRestorePasskey,
    prepareWalletPasskeyAction,
    type WalletPasskeyStep,
} from 'lib/walletPasskeyRecovery'
import {
    passkeyBrowserUnavailableReason,
    walletPasskeyErrorMessage,
} from 'lib/walletPasskeyWebAuthn'

const NOTICE_KIND = 'wallet-passkey-restore'
// Server ceremonies expire after 5 minutes; past this, prepare a new one before prompting.
const STEP_MAX_AGE_MS = 4 * 60 * 1000

let inFlight: Promise<boolean> | null = null

/**
 * When this browser lost its local wallet keys (site data cleared, storage evicted), offer
 * the restore. Browser-wallet signups get their full-screen unlock (WalletSetupHost);
 * every other self-custody account with a verified passkey backup gets a one-tap notice.
 * Resolves true when a restore is on screen, false when there is nothing to offer.
 * `fromAction` reopens the unlock screen even after the user chose "Not now" on it.
 */
export function offerWalletPasskeyRestore({ fromAction = false } = {}): Promise<boolean> {
    if (useStore.getState().globalNotice?.kind === NOTICE_KIND) return Promise.resolve(true)
    if (!inFlight) {
        inFlight = prepareOffer(undefined, fromAction).finally(() => {
            inFlight = null
        })
    }
    return inFlight
}

async function prepareOffer(message?: string, fromAction = false): Promise<boolean> {
    const userId = window.gon?.currentUser?.id
    if (!userId || !currentUserUsesClientSigning()) return false
    if (await getClientShare(userId)) return false

    if (await isBrowserWalletAccount()) {
        // WalletSetupHost already showed its unlock screen on load.
        if (fromAction) window.dispatchEvent(new Event(OPEN_WALLET_SETUP_EVENT))
        return fromAction
    }
    if (passkeyBrowserUnavailableReason()) return false

    // An account without a wallet yet (mid-signup) has no status to load and nothing to restore.
    const status = await loadWalletPasskeyStatus().catch(() => null)
    if (!status) return false
    const passkey = pickRestorePasskey(status)
    if (!passkey) return false
    const step = await prepareWalletPasskeyAction(passkey, status.identity, 'recover')
    showOffer(step, Date.now(), message)
    return true
}

async function isBrowserWalletAccount(): Promise<boolean> {
    try {
        await loadLocalWalletStatus()
        return true
    } catch (error) {
        if (error instanceof NotLocalWalletAccountError) return false
        throw error
    }
}

function showOffer(step: WalletPasskeyStep, preparedAt: number, message?: string) {
    useStore.getState().showGlobalNotice(message || 'Restore your wallet on this device', {
        detail: 'This browser lost its saved wallet keys. Approve your passkey to bring them back.',
        variant: 'info',
        durationMs: Infinity,
        kind: NOTICE_KIND,
        testId: 'wallet-passkey-restore',
        actionLabel: 'Restore',
        cancelLabel: 'Not now',
        onAction: () => restore(step, preparedAt),
    })
}

function restore(step: WalletPasskeyStep, preparedAt: number) {
    const { showGlobalNotice, updateGlobalNotice } = useStore.getState()
    if (Date.now() - preparedAt > STEP_MAX_AGE_MS) {
        void prepareOffer('Tap Restore again to approve your passkey').catch(showFailure)
        return
    }

    // Start the passkey prompt inside the click so the browser keeps its user activation.
    const recovery = advanceWalletPasskeyStep(step)
    showGlobalNotice('Restoring your wallet…', {
        variant: 'pending',
        kind: NOTICE_KIND,
        testId: 'wallet-passkey-restore',
    })
    recovery
        .then(() => {
            window.dispatchEvent(new Event(CLIENT_SIGNING_READY_EVENT))
            updateGlobalNotice(NOTICE_KIND, 'Wallet restored on this device', {
                variant: 'success',
                detail: 'You can upvalue and send again.',
            })
        })
        .catch(showFailure)
}

// The picked passkey may not be on this device, so a retry goes to the wallet modal:
// it lists every passkey backup and the wallet file restore.
function showFailure(error: unknown) {
    useStore.getState().showGlobalNotice(walletPasskeyErrorMessage(error), {
        variant: 'error',
        durationMs: Infinity,
        kind: NOTICE_KIND,
        testId: 'wallet-passkey-restore',
        actionLabel: 'Open wallet',
        cancelLabel: 'Close',
        onAction: () => {
            useStore.getState().actions?.modalActions?.openWalletModal?.()
        },
    })
}
